import React, { useEffect, useState } from "react";
import { Button, Modal, message, Select, Tag, DatePicker, Space } from 'antd';
import { Form, InputGroup, Badge } from 'react-bootstrap';
import { ShoppingCartOutlined, CheckCircleOutlined, ClockCircleOutlined, DollarOutlined, DownloadOutlined, EyeOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import { oneKlicks } from "../../../SDK/oneclick";
import { getuserdata } from "../../../services/apiService";
import Paginationnew from "../../../Uitiles/Paginationnew";

const { Option } = Select;
const { RangePicker } = DatePicker;

const OrderHistory = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterStatus, setFilterStatus] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [dateRange, setDateRange] = useState({
        startDate: "",
        endDate: ""
    });
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
        loadOrders();
    }, [page, searchTerm, filterStatus, dateRange]);

    const loadOrders = async () => {
        try {
            setLoading(true);
            
            // Get pharmacy ID from user data
            const pharmacyId = await getuserdata("id");
            if (!pharmacyId) {
                message.error("Pharmacy ID not found. Please login again.");
                setLoading(false);
                return;
            }

            // Fetch orders from database with pagination
            const tableSchema = "";
            const tableName = "pharmacy_orders";
            const conditions = { 
                pharmacy_id: pharmacyId,
                // Filter by status if not "all"
                ...(filterStatus !== "all" && { status: filterStatus })
            };
            
            // Add date range conditions if provided
            if (dateRange.startDate) {
                conditions.order_date_gte = typeof dateRange.startDate === 'string' ? dateRange.startDate : dateRange.startDate.format('YYYY-MM-DD');
            }
            if (dateRange.endDate) {
                conditions.order_date_lte = typeof dateRange.endDate === 'string' ? dateRange.endDate : dateRange.endDate.format('YYYY-MM-DD');
            }

            const orderBy = "id DESC";
            const search = {
                ...searchTerm && {
                    order_number: searchTerm,
                    customer_name: searchTerm,
                    customer_email: searchTerm
                }
            };
            const pagination = {
                page: page,
                limit: limit
            };
            const margedata = [];
            const range = [];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata, range);

            if (!response) {
                throw new Error('No response from server');
            }

            if (response.success === false) {
                console.error('API returned error:', response);
                message.error(response.message || 'Failed to fetch orders');
                setOrders([]);
                setTotalPages(0);
                setLoading(false);
                return;
            }

            if (!response.data) {
                setOrders([]);
                setTotalPages(0);
                setLoading(false);
                return;
            }

            // Transform the data to match the expected format
            const transformedOrders = response.data.map(order => ({
                id: order.id,
                orderNumber: order.order_number || order.orderNumber || `ORD-${order.id}`,
                customerName: order.customer_name || order.customerName || 'N/A',
                customerPhone: order.customer_phone || order.customerPhone || 'N/A',
                customerEmail: order.customer_email || order.customerEmail || 'N/A',
                orderDate: order.order_date || order.orderDate || order.created_at || new Date().toISOString(),
                deliveryDate: order.delivery_date || order.deliveryDate || null,
                status: order.status || 'pending',
                totalAmount: parseFloat(order.total_amount || order.totalAmount || 0),
                items: Array.isArray(order.items) ? order.items : (typeof order.items === 'string' ? JSON.parse(order.items || '[]') : []),
                prescriptionRequired: Boolean(order.prescription_required || order.prescriptionRequired),
                prescriptionImage: order.prescription_image || order.prescriptionImage || '',
                deliveryAddress: order.delivery_address || order.deliveryAddress || '',
                notes: order.notes || "",
                paymentMethod: order.payment_method || order.paymentMethod || 'N/A',
                paymentStatus: order.payment_status || order.paymentStatus || 'pending',
                trackingNumber: order.tracking_number || order.trackingNumber || null
            }));
            
            setOrders(transformedOrders);
            setTotalPages(response.totalPages || 1);
            setLoading(false);
        } catch (error) {
            console.error("Error loading orders:", error);
            message.error("Failed to load orders. Please try again.");
            setOrders([]);
            setTotalPages(0);
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "pending": return "orange";
            case "confirmed": return "blue";
            case "processing": return "purple";
            case "shipped": return "green";
            case "delivered": return "success";
            case "cancelled": return "red";
            default: return "default";
        }
    };

    const getPaymentStatusColor = (status) => {
        switch (status) {
            case "paid": return "success";
            case "pending": return "warning";
            case "failed": return "error";
            case "refunded": return "processing";
            default: return "default";
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const exportToCSV = () => {
        try {
            const csvContent = [
                ["Order Number", "Customer Name", "Order Date", "Status", "Total Amount", "Payment Status", "Items Count"],
                ...orders.map(order => [
                    order.orderNumber,
                    order.customerName,
                    formatDate(order.orderDate),
                    order.status,
                    order.totalAmount.toFixed(2),
                    order.paymentStatus,
                    order.items.length
                ])
            ].map(row => row.join(",")).join("\n");

            const blob = new Blob([csvContent], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `order-history-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            message.success("Order history exported successfully");
        } catch (error) {
            console.error("Error exporting CSV:", error);
            message.error("Failed to export order history");
        }
    };

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setShowOrderModal(true);
    };

    const handleDateRangeChange = (dates, dateStrings) => {
        if (dates && dates.length === 2) {
            setDateRange({
                startDate: dates[0],
                endDate: dates[1]
            });
        } else {
            setDateRange({
                startDate: "",
                endDate: ""
            });
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const getOrderSummaryStats = () => {
        const delivered = orders.filter(o => o.status === "delivered").length;
        const cancelled = orders.filter(o => o.status === "cancelled").length;
        const totalRevenue = orders.reduce((sum, order) => sum + order.totalAmount, 0);
        const total = orders.length;

        return { total, delivered, cancelled, totalRevenue };
    };

    const stats = getOrderSummaryStats();

    return (
        <>
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3" style={{ color: 'white' }}>Order History</h5>
                                <Badge bg="info">History</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="flex-grow-1 me-2 h-40">
                                    <Form.Control
                                        placeholder="Search by order number, customer name, or email..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </InputGroup>
                                <Select
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    style={{ width: 150, marginRight: '12px' }}
                                >
                                    <Option value="all">All Orders</Option>
                                    <Option value="delivered">Delivered</Option>
                                    <Option value="cancelled">Cancelled</Option>
                                    <Option value="shipped">Shipped</Option>
                                    <Option value="processing">Processing</Option>
                                    <Option value="confirmed">Confirmed</Option>
                                    <Option value="pending">Pending</Option>
                                </Select>
                                <RangePicker
                                    style={{ marginRight: '12px' }}
                                    onChange={handleDateRangeChange}
                                    format="YYYY-MM-DD"
                                />
                                <Button className="btn-success h-40" onClick={exportToCSV}>
                                    <DownloadOutlined /> Export CSV
                                </Button>
                                <Button className="btn-primary h-40" onClick={loadOrders} loading={loading}>
                                    <ReloadOutlined /> Refresh
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Order Summary Stats */}
                <div className="row mb-4">
                    <div className="col-md-3 col-sm-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: 'white' }}>Delivered</h6>
                                <h4 className="mb-0" style={{ color: 'white' }}>{stats.delivered}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <ClockCircleOutlined style={{ color: '#ff4d4f', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: 'white' }}>Cancelled</h6>
                                <h4 className="mb-0" style={{ color: 'white' }}>{stats.cancelled}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <DollarOutlined style={{ color: '#1890ff', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: 'white' }}>Total Revenue</h6>
                                <h4 className="mb-0" style={{ color: 'white' }}>${stats.totalRevenue.toFixed(2)}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <ShoppingCartOutlined style={{ color: '#1890ff', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: 'white' }}>Total Orders</h6>
                                <h4 className="mb-0" style={{ color: 'white' }}>{stats.total}</h4>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table hover size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Order Number</th>
                                        <th>Customer</th>
                                        <th>Order Date</th>
                                        <th>Delivery Date</th>
                                        <th>Status</th>
                                        <th>Total Amount</th>
                                        <th>Payment</th>
                                        <th>Items</th>
                                        <th style={{ width: '200px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="10" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : orders.length === 0 ? (
                                        <tr>
                                            <td colSpan="10" className="text-center py-5">
                                                <ShoppingCartOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                                                <h5 className="mb-2" style={{ color: 'white' }}>No orders found</h5>
                                                <p className="mb-3" style={{ color: 'white' }}>
                                                    {searchTerm ? 'Try adjusting your search criteria' : 'No order history available'}
                                                </p>
                                            </td>
                                        </tr>
                                    ) : (
                                        orders.map((item) => (
                                            <tr key={item.id}>
                                                <td data-label="ID" style={{ color: 'white' }}>{item.id}</td>
                                                <td data-label="Order Number" style={{ fontWeight: '600', color: 'white' }}>
                                                    {item.orderNumber}
                                                    {item.trackingNumber && (
                                                        <div style={{ fontSize: '11px', color: 'white', marginTop: '2px' }}>
                                                            Track: {item.trackingNumber}
                                                        </div>
                                                    )}
                                                </td>
                                                <td data-label="Customer">
                                                    <div>
                                                        <div style={{ fontWeight: '600', marginBottom: '2px', color: 'white' }}>
                                                            {item.customerName}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: 'white' }}>{item.customerEmail}</div>
                                                        <div style={{ fontSize: '12px', color: 'white' }}>{item.customerPhone}</div>
                                                    </div>
                                                </td>
                                                <td data-label="Order Date" style={{ color: 'white' }}>{formatDate(item.orderDate)}</td>
                                                <td data-label="Delivery Date" style={{ color: 'white' }}>{formatDate(item.deliveryDate)}</td>
                                                <td data-label="Status">
                                                    <Tag 
                                                        color={getStatusColor(item.status)} 
                                                        style={{ 
                                                            borderRadius: '6px',
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            padding: '4px 8px'
                                                        }}
                                                    >
                                                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                                    </Tag>
                                                </td>
                                                <td data-label="Total Amount" style={{ fontWeight: '600', color: 'white' }}>
                                                    ${item.totalAmount.toFixed(2)}
                                                </td>
                                                <td data-label="Payment">
                                                    <div>
                                                        <Tag 
                                                            color={getPaymentStatusColor(item.paymentStatus)} 
                                                            style={{ marginBottom: '4px', display: 'block' }}
                                                        >
                                                            {item.paymentStatus.charAt(0).toUpperCase() + item.paymentStatus.slice(1)}
                                                        </Tag>
                                                        <div style={{ fontSize: '11px', color: 'white' }}>{item.paymentMethod}</div>
                                                    </div>
                                                </td>
                                                <td data-label="Items" style={{ color: 'white' }}>
                                                    {item.items.length} items
                                                    {item.prescriptionRequired && (
                                                        <Badge bg="danger" style={{ marginLeft: '4px' }}>Rx</Badge>
                                                    )}
                                                </td>
                                                <td data-label="Action">
                                                    <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                        <Button href="#" className="dc-update-servicesss fit-content btn-info" onClick={(e) => {
                                                            e.preventDefault();
                                                            handleViewOrder(item);
                                                        }}>
                                                            <EyeOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                        </Button>
                                                        {item.status === "delivered" && (
                                                            <Button href="#" className="dc-update-servicesss fit-content btn-success" onClick={(e) => {
                                                                e.preventDefault();
                                                                message.info("Invoice reprint functionality coming soon");
                                                            }}>
                                                                <PrinterOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                    </div>
                </div>

                {/* Order Details Modal */}
                <Modal
                    title="Order Details"
                    open={showOrderModal}
                    onCancel={() => setShowOrderModal(false)}
                    footer={[
                        <Button key="close" onClick={() => setShowOrderModal(false)}>
                            Close
                        </Button>
                    ]}
                    width={800}
                >
                    {selectedOrder && (
                        <div>
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: 'white' }}>Order #{selectedOrder.orderNumber}</h4>
                                {selectedOrder.trackingNumber && (
                                    <p style={{ color: 'white', marginTop: '5px' }}>
                                        <strong>Tracking Number:</strong> {selectedOrder.trackingNumber}
                                    </p>
                                )}
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginTop: '15px' }}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p style={{ color: 'white' }}><strong>Customer Name:</strong> {selectedOrder.customerName}</p>
                                            <p style={{ color: 'white' }}><strong>Email:</strong> {selectedOrder.customerEmail}</p>
                                            <p style={{ color: 'white' }}><strong>Phone:</strong> {selectedOrder.customerPhone}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p style={{ color: 'white' }}><strong>Order Date:</strong> {formatDate(selectedOrder.orderDate)}</p>
                                            <p style={{ color: 'white' }}><strong>Delivery Date:</strong> {formatDate(selectedOrder.deliveryDate)}</p>
                                            <p style={{ color: 'white' }}><strong>Status:</strong> 
                                                <Tag color={getStatusColor(selectedOrder.status)} style={{ marginLeft: '8px' }}>
                                                    {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                                                </Tag>
                                            </p>
                                            <p style={{ color: 'white' }}><strong>Total Amount:</strong> ${selectedOrder.totalAmount.toFixed(2)}</p>
                                            <p style={{ color: 'white' }}><strong>Payment Status:</strong> 
                                                <Tag color={getPaymentStatusColor(selectedOrder.paymentStatus)} style={{ marginLeft: '8px' }}>
                                                    {selectedOrder.paymentStatus.charAt(0).toUpperCase() + selectedOrder.paymentStatus.slice(1)}
                                                </Tag>
                                            </p>
                                            <p style={{ color: 'white' }}><strong>Payment Method:</strong> {selectedOrder.paymentMethod}</p>
                                        </div>
                                    </div>
                                    {selectedOrder.deliveryAddress && (
                                        <p style={{ color: 'white' }}><strong>Delivery Address:</strong> {selectedOrder.deliveryAddress}</p>
                                    )}
                                    {selectedOrder.notes && (
                                        <p style={{ color: 'white' }}><strong>Notes:</strong> {selectedOrder.notes}</p>
                                    )}
                                    {selectedOrder.prescriptionRequired && (
                                        <p style={{ color: 'white' }}>
                                            <strong>Prescription Required:</strong> 
                                            <Badge bg="danger" style={{ marginLeft: '8px' }}>Yes</Badge>
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <h5 style={{ color: 'white' }}>Order Items</h5>
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th style={{ color: 'white' }}>Item</th>
                                                <th style={{ color: 'white' }}>Quantity</th>
                                                <th style={{ color: 'white' }}>Price</th>
                                                <th style={{ color: 'white' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedOrder.items && selectedOrder.items.length > 0 ? (
                                                selectedOrder.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td style={{ color: 'white' }}>{item.name || item.product_name || 'N/A'}</td>
                                                        <td style={{ color: 'white' }}>{item.quantity || 1}</td>
                                                        <td style={{ color: 'white' }}>${parseFloat(item.price || 0).toFixed(2)}</td>
                                                        <td style={{ color: 'white' }}>${(parseFloat(item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center" style={{ color: 'white' }}>No items found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </>
    );
};

export default OrderHistory;
